import { useMemo } from 'react';
import { ItemCategory, ItemCommissionType } from '@prisma/client';
import { Box, capitalize } from '@mui/material';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import { Form, Formik } from 'formik';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import CollapsibleSection from '../../form/AcceptRequestFlow/CollapsibleSection';
import { InputRowType } from '../../../interfaces';
import useUser from '../../../hooks/global/useUser';
import useAttributes from '../../../hooks/catalog/useAttributes';
import useBrands from '../../../hooks/catalog/useBrands';
import useProduct from '../../../hooks/products/useProduct';
import useSaveProduct from '../../../hooks/products/useSaveProduct';
import useGetOnboarding from '../../../hooks/businesses/useGetOnboarding';
import InputGroup from '../../form/InputGroup';
import ImageInput from '../../form/ImageInput';
import PageLoading from '../../global/PageLoading';
import Error from '../../global/Error';
import { getCommissionEmployees } from '../../../helpers/items';
import { ItemWithCommissions } from '../../../types/items';
import { ImageItem } from '../../../utils/form/appointmentForm';
import { roundToMaxDecimalPlaces } from '../../../utils/global/helper';
import { PERCENT_MAX_TAX_DECIMAL_PLACES } from '../../../types/businesses';
import {
  EMPTY_PRODUCT_FORM,
  ProductFormValues,
  attributesForCategory,
  emptyVariantRow,
  formValuesFromItem,
  formValuesFromProduct,
  productFormSchema,
  typeLabelsFor,
  withCommissionRate,
} from '../../../utils/catalog/productForm';
import PickerField from './PickerField';
import AttributeFields from './AttributeFields';
import VariantRows from './VariantRows';
import { commissionRateRows } from './commissionRows';
import VariantMoneyField from './VariantMoneyField';

/** One cost shows as an amount; several are summarized. */
function costSummary(variants: ProductFormValues['variants']) {
  const costs = variants.map(variant => variant.cost);
  if (!costs.some(cost => typeof cost === 'number')) return [];
  return costs.length === 1 ? [`Cost $${costs[0]}`] : ['Costs set'];
}

interface Props {
  /** Edit an existing product. */
  productId?: string | null;
  /** Convert a standalone item into a product on save. */
  standaloneItem?: ItemWithCommissions | null;
  onSuccess?: () => void;
}

export default function ProductForm({
  productId,
  standaloneItem,
  onSuccess,
}: Props) {
  const { user } = useUser();
  const { reloadAfterUpdate } = useGetOnboarding();

  const attributesQuery = useAttributes();
  const brandsQuery = useBrands();
  const productQuery = useProduct(productId);
  const attributes = useMemo(
    () => attributesQuery.data ?? [],
    [attributesQuery.data],
  );
  const brandOptions = useMemo(
    () => (brandsQuery.data ?? []).map(brand => brand.name),
    [brandsQuery.data],
  );

  const initialValues = useMemo<ProductFormValues>(() => {
    if (productQuery.data) return formValuesFromProduct(productQuery.data);
    if (standaloneItem) return formValuesFromItem(standaloneItem);
    return {
      ...EMPTY_PRODUCT_FORM,
      isTaxable: (user?.business?.businessTaxRates?.productTaxRate ?? 0) > 0,
    };
  }, [
    productQuery.data,
    standaloneItem,
    user?.business?.businessTaxRates?.productTaxRate,
  ]);

  const detailsOpen =
    Object.keys(initialValues.attributeValues).length > 0 ||
    initialValues.varyingKeys.length > 0;
  const costsOpen =
    initialValues.variants.some(variant => variant.cost !== '') ||
    initialValues.employeeCommissions.some(c => c.rate > 0);

  const { mutateAsync: saveProduct, isPending } = useSaveProduct({
    onSuccess: (_, type) => {
      reloadAfterUpdate('ITEM_LIBRARY');
      toast.success(`Item ${type === 'create' ? 'created' : 'updated'}!`);
      onSuccess?.();
    },
    onError: message => toast.error(message),
  });

  const productTaxRate = user?.business?.businessTaxRates?.productTaxRate || 0;
  const productTaxPercent = roundToMaxDecimalPlaces(
    productTaxRate * 100,
    PERCENT_MAX_TAX_DECIMAL_PLACES,
  );
  const detailsHintFor = (category: ProductFormValues['category']) =>
    attributesForCategory(attributes, category)
      .slice(0, 3)
      .map(a => a.label.toLowerCase())
      .join(', ') || 'options';

  const detailChips = (current: ProductFormValues) => [
    ...Object.entries(current.attributeValues)
      .filter(([, value]) => !!value)
      .map(
        ([key, value]) =>
          `${attributes.find(a => a.key === key)?.label ?? key}: ${value}`,
      ),
    ...current.varyingKeys.map(
      key => `${attributes.find(a => a.key === key)?.label ?? key} varies`,
    ),
  ];
  const costChips = (current: ProductFormValues) => [
    ...costSummary(current.variants),
    ...(current.employeeCommissions.some(c => c.rate > 0)
      ? ['Commissions set']
      : []),
  ];

  const commissionEmployees =
    user?.business?.type === 'STUDIO'
      ? getCommissionEmployees(user?.business?.employees)
      : [];

  if (productId && productQuery.isError) {
    return <Error errorMessage="Could not load this item" />;
  }
  if ((productId && !productQuery.data) || attributesQuery.isLoading) {
    return <PageLoading />;
  }

  return (
    <Box>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={productFormSchema}
        onSubmit={async values => {
          try {
            await saveProduct({
              productId,
              values,
              existingItemId: standaloneItem?.id,
            });
          } catch {
            // surfaced by onError
          }
        }}>
        {({ values, isValid, isSubmitting, dirty, setFieldValue }) => (
          <Form>
            <InputGroup
              name="Item Details"
              rows={[
                [
                  {
                    label: 'Name *',
                    name: 'name',
                    type: 'text',
                    placeholder: 'Enter item name',
                  },
                ],
                [
                  {
                    label: 'Category *',
                    name: 'category',
                    type: 'select',
                    placeholder: 'Select category',
                    options: Object.values(ItemCategory)
                      .filter(category => category !== ItemCategory.GIFT_CARD)
                      .map(category => ({
                        label: capitalize(category.toLowerCase()),
                        value: category,
                      })),
                    autoWidth: false,
                    // Attribute fields follow the category, so stale values are dropped
                    onChange: () => {
                      setFieldValue('typeLabel', '');
                      setFieldValue('brandName', '');
                      setFieldValue('attributeValues', {});
                      setFieldValue('varyingKeys', []);
                      setFieldValue('axisValues', {});
                      setFieldValue('variants', [emptyVariantRow()]);
                    },
                  },
                ],
                [
                  {
                    label: 'Type',
                    name: 'typeLabel',
                    type: 'node',
                    node: (
                      <PickerField
                        name="typeLabel"
                        label="Type"
                        value={values.typeLabel}
                        options={typeLabelsFor(attributes, values.category)}
                        placeholder={
                          values.category
                            ? 'Select type'
                            : 'Pick a category first'
                        }
                        disabled={!values.category}
                        allowCreate={false}
                        onChange={value => setFieldValue('typeLabel', value)}
                      />
                    ),
                  },
                ],
                [
                  {
                    label: 'Brand',
                    name: 'brandName',
                    type: 'node',
                    node: (
                      <PickerField
                        name="brandName"
                        label="Brand"
                        value={values.brandName}
                        options={brandOptions}
                        placeholder="Select or add a brand"
                        onChange={value => setFieldValue('brandName', value)}
                      />
                    ),
                  },
                ],
                [
                  {
                    label: 'Description',
                    name: 'description',
                    type: 'textarea',
                    placeholder: 'Optional description',
                  },
                ],
                [
                  {
                    label: 'Photo',
                    name: 'images',
                    type: 'node',
                    node: (
                      <ImageInput
                        name="productImage"
                        label="Photo"
                        images={values.images as ImageItem[]}
                        setImages={updater => {
                          const current = values.images as ImageItem[];
                          const next =
                            typeof updater === 'function'
                              ? updater(current)
                              : updater;
                          setFieldValue('images', next);
                        }}
                        maxImages={1}
                      />
                    ),
                  },
                ],
              ]}
            />

            <InputGroup
              name="Pricing"
              rows={[
                [
                  {
                    label: 'Price *',
                    name:
                      values.variants.length === 1
                        ? 'variants.0.price'
                        : 'priceForAllVariants',
                    type: 'node',
                    node: <VariantMoneyField field="price" label="Price *" />,
                  },
                ],
                [
                  {
                    label: 'Sales Tax *',
                    name: 'isTaxable',
                    type: 'select',
                    placeholder: 'Select sales tax',
                    options: [
                      { label: 'Non-taxable item', value: false },
                      {
                        label: `Collect product sales tax (${productTaxPercent}%)`,
                        value: true,
                      },
                    ],
                    autoWidth: false,
                  },
                ],
              ]}
            />

            {!!values.category && (
              <CollapsibleSection
                title="Details"
                subtitle={`Optional: ${detailsHintFor(values.category)}`}
                icon={<TuneRoundedIcon sx={{ color: 'secondary.main' }} />}
                chips={detailChips(values)}
                defaultExpanded={detailsOpen}>
                <AttributeFields attributes={attributes} />
              </CollapsibleSection>
            )}

            <CollapsibleSection
              title="Cost & payouts"
              subtitle="Optional: what it costs you and who earns commission"
              icon={<PaymentsRoundedIcon sx={{ color: 'secondary.main' }} />}
              chips={costChips(values)}
              defaultExpanded={costsOpen}>
              <InputGroup
                name=""
                rows={
                  [
                    [
                      {
                        label: 'Cost',
                        name:
                          values.variants.length === 1
                            ? 'variants.0.cost'
                            : 'costForAllVariants',
                        type: 'node',
                        node: (
                          <VariantMoneyField
                            field="cost"
                            label="Cost"
                            tooltip="The cost to stock this item"
                          />
                        ),
                      },
                    ],
                    commissionEmployees.length
                      ? [
                          {
                            label: 'Commission Type *',
                            name: 'commissionType',
                            type: 'select',
                            placeholder: 'Select commission type',
                            disabled: !values.employeeCommissions.some(
                              c => c.rate > 0,
                            ),
                            options: [
                              {
                                label: 'Pay commissions on Revenue',
                                value: ItemCommissionType.REVENUE,
                              },
                              {
                                label: 'Pay commissions on Profit',
                                value: ItemCommissionType.PROFIT,
                              },
                            ],
                            autoWidth: false,
                          },
                        ]
                      : null,
                    ...commissionRateRows(
                      commissionEmployees,
                      values.employeeCommissions,
                      (employeeId, percent) =>
                        setFieldValue(
                          'employeeCommissions',
                          withCommissionRate(
                            values.employeeCommissions,
                            employeeId,
                            percent,
                          ),
                        ),
                    ),
                  ].filter(Boolean) as InputRowType[]
                }
              />
            </CollapsibleSection>

            {!!values.category && values.varyingKeys.length > 0 && (
              <VariantRows attributes={attributes} />
            )}

            <LoadingButton
              type="submit"
              disabled={
                !isValid || isSubmitting || isPending || (!!productId && !dirty)
              }
              loading={isPending || isSubmitting}
              variant="contained"
              className="primary">
              {productId ? 'Save' : 'Create'}
            </LoadingButton>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
